/*
* Regional Component List Manager - allows users to manage regional component lists
*/

import '@progress/kendo-ui';

import { orderBy, process } from '@progress/kendo-data-query';
import { Grid, GridCell, GridColumn, GridToolbar } from '@progress/kendo-react-grid';
import * as _ from 'lodash';
import * as React from 'react';
import { Button, ButtonGroup, ButtonToolbar, Modal, Row, Well } from 'react-bootstrap';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { push } from 'react-router-redux';
import { compose } from 'redux';
import { connectRequest } from 'redux-query';

import * as MutateActions from '../actions/mutate';
import * as AppComp from '../common/app-component-base';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../common/formatters';
import { NavigationLinks } from '../common/navigation';
import { WhiteBoxPageSection } from '../common/whiteboxpagesection';
import ApiEndpoints from '../config/apiendpoints';
import * as LK from '../config/apilookupkeys';
import * as ReduxQueries from '../queries';

export interface IRegionalComponentListManagerProps extends AppComp.IAppComponentProps {
    forceRequest?: any;
    navigateTo?: any;
    lookups?: any;
    regionalComponentLists?: any;
    deleteRegionalComponentListObj?: any;
    duplicateRegionalComponentListObj?: any;
    newRegionalComponentList?: any;
}

export interface IRegionalComponentListManagerState extends AppComp.IAppComponentState {
    editData?: any;
    gridSort?: any;
    showModalDelete: boolean;
}

class RegionalComponentListManager extends AppComp.AppComponent<IRegionalComponentListManagerProps, IRegionalComponentListManagerState> {
    public readonly GridEditField: string = 'editField';
    public readonly GridNewRecordField: string = 'newRecordField';

    public constructor(props: IRegionalComponentListManagerProps) {
        super(props, 'app.regional-component-list-manager.');

        this.state = {
            editData: {},
            gridSort: undefined,
            showModalDelete: false,
        };
    }

    // BUTTON HANDLERS
    public handleEnterEdit(d: any) {
        const listId = Formatters.getStr(d, '_id');
        if (listId) {
            this.props.navigateTo(NavigationLinks.regionalComponentListEdit(listId));
        }
    }

    public handleEnterDetails(d: any) {
        // Reserved for future use
    }

    public handleAddNewButton(e: any) {
        const token = Formatters.getStr(this.props, 'authsession.access_token', '');
        if (!token) { return; }
        // Create a new list via API, then navigate to edit page with the new list ID
        const changeData = {};
        this.props.newRegionalComponentList(changeData, token, false).then((response: any) => {
            if (response.status >= 200 && response.status < 300) {
                const newListId = Formatters.getStr(response.body, 'regionalComponentList.document._id');
                if (newListId) {
                    this.props.navigateTo(NavigationLinks.regionalComponentListEdit(newListId));
                }
            }
        });
    }

    public handleDuplicate(d: any) {
        const token = Formatters.getStr(this.props, 'authsession.access_token', '');
        const regionalComponentListId = Formatters.getStr(d, '_id');
        if (!token || !regionalComponentListId) { return; }

        const changeData = { regionalComponentListId };
        this.props.duplicateRegionalComponentListObj(changeData, token, false).then((response: any) => {
            if (response.status >= 200 && response.status < 300) {
                const newListId =
                    Formatters.getStr(response.body, 'regionalComponentList.document._id') ||
                    Formatters.getStr(response.body, 'regionalComponentList._id') ||
                    Formatters.getStr(response.body, 'newRegionalComponentList.document._id') ||
                    Formatters.getStr(response.body, 'newRegionalComponentList._id');

                if (newListId) {
                    this.props.navigateTo(NavigationLinks.regionalComponentListEdit(newListId));
                } else {
                    this.props.forceRequest();
                }
            }
        });
    }

    public handleShowModalDelete(e: any, selectedDataItem: any) {
        this.setState({ showModalDelete: true, editData: _.cloneDeep(selectedDataItem) });
    }

    public handleCloseModalDelete() {
        this.setState({ showModalDelete: false, editData: {} });
    }

    public handleConfirmDelete(e: any) {
        const token = Formatters.getStr(this.props, 'authsession.access_token');
        const regionalComponentListId = Formatters.getStr(this.state.editData, '_id');

        const changeData = _.merge(
            {},
            { regionalComponentListId: regionalComponentListId },
        );

        this.props.deleteRegionalComponentListObj(changeData, token, false).then(response => {
            if (response.status >= 200 && response.status < 300) {
                this.setState({
                    editData: {},
                    showModalDelete: false,
                });
                this.props.forceRequest();
            }
        });
    }

    public handleSave(d: any) {
        // Not used in list view - edit navigates to edit page
    }

    public handleCreate(d: any) {
        // Not used in list view
    }

    public handleCancel(d: any) {
        // Not used in list view
    }

    public handleGridItemChange(e: any) {
        // Not used in list view
    }

    // HELPERS
    public getGridCol(fieldName: string, colTitle: string, colOptions: any = {}) {
        const result = _.merge(
            {},
            {
                f: fieldName,
                t: colTitle,
                p: colOptions,
            });

        return result;
    }

    public hasAllValues(dataItem: any, vals: Array<any> = []) {
        if (!_.size(vals)) { return true; }

        return _.every(vals, (v) => {
            const val = Formatters.getStr(dataItem, v);
            return !_.isEmpty(val);
        });
    }

    public gridCommandCellDetailsButtonEnabled(dataItem: any) {
        return false;
    }

    public gridCommandCellDeleteButtonEnabled(dataItem: any) {
        return true;
    }

    public getDescriptionPreview(description: string, maxLength: number = 50) {
        const cleanDescription = String(description || '').replace(/\s+/g, ' ').trim();
        return cleanDescription.length > maxLength
            ? `${cleanDescription.substr(0, maxLength)}...`
            : cleanDescription;
    }

    public extendGridData(records: Array<any>) {
        if (!records) { return []; }

        return records.map(r => {
            let record = _.cloneDeep(r);

            // Extract ID
            record.id = Formatters.getStr(record, '_id');

            // Extract Region name
            record.regionName = Formatters.getStr(record, 'region.name');

            // Extract List Type (use listType field if available, otherwise fall back to name)
            record.listType = Formatters.getStr(record, 'listType') || Formatters.getStr(record, 'name');

            record.descriptionPreview = this.getDescriptionPreview(Formatters.getStr(record, 'description'));

            return record;
        });
    }

    // RENDERS
    public renderHeaderButtonToolbar() {
        return (
            <div>
                <Well className="header-well-buttontoolbar">
                    <Row>
                        <Col xs={3}>
                            <ButtonToolbar>
                                <ButtonGroup>
                                    <Button bsSize="sm" bsStyle="success" onClick={(e) => this.handleAddNewButton(e)}>
                                        <i className="glyphicon glyphicon-plus" /> Create New List
                                    </Button>
                                </ButtonGroup>
                            </ButtonToolbar>
                        </Col>
                    </Row>
                </Well>
            </div>
        );
    }

    public renderGridCol(c: any, i?: any) {
        let props: any = {
            key: `col-${i}`,
        };

        if (Formatters.getStr(c, 'f')) { _.merge(props, { field: Formatters.getStr(c, 'f') }); }
        if (Formatters.getStr(c, 't')) { _.merge(props, { title: Formatters.getStr(c, 't') }); }
        if (_.get(c, 'p', {})) { _.merge(props, _.get(c, 'p', {})); }

        let result = (<GridColumn {...props} />);

        return result;
    }

    public renderDescriptionPreviewCell(maxLength: number = 50) {
        const getDescriptionPreview = (description: string) => this.getDescriptionPreview(description, maxLength);

        return class extends GridCell {
            public render() {
                const { dataItem } = this.props;
                const description = Formatters.getStr(dataItem, 'description');

                return (
                    <td title={description}>
                        {getDescriptionPreview(description)}
                    </td>
                );
            }
        };
    }

    public renderGridCommandCell() {
        const handleEnterEdit = (dataItem: any) => this.handleEnterEdit(dataItem);
        const handleDuplicate = (dataItem: any) => this.handleDuplicate(dataItem);
        const handleRemove = (e: any, dataItem: any) => this.handleShowModalDelete(e, dataItem);
        const showDelete = (dataItem: any) => this.gridCommandCellDeleteButtonEnabled(dataItem);
        const appTextCommon = (key: string) => this.appTextCommon(key);

        return class extends GridCell {
            public render() {
                const { dataItem } = this.props;
                const buttonProps = {
                    bsSize: 'xs' as any,
                };
                const showDeleteButton = showDelete(dataItem);

                return (
                    <td>
                        <ButtonToolbar>
                            <Button
                                {...buttonProps}
                                bsStyle="info"
                                onClick={(e) => handleEnterEdit(dataItem)}
                            >
                                <i className="fa fa-pencil" /> <span>{appTextCommon('buttons.edit')}</span>
                            </Button>
                            <Button
                                {...buttonProps}
                                bsStyle="warning"
                                onClick={(e) => handleDuplicate(dataItem)}
                            >
                                <i className="glyphicon glyphicon-copy" /> <span>{appTextCommon('buttons.duplicate')}</span>
                            </Button>
                            {(showDeleteButton) ? (
                                <Button
                                    {...buttonProps}
                                    bsStyle="danger"
                                    disabled={!showDeleteButton}
                                    onClick={(e) => handleRemove(e, dataItem)}
                                >
                                    <i className="fa fa-trash-o" /> <span>{appTextCommon('buttons.delete')}</span>
                                </Button>) : (<div />)}
                        </ButtonToolbar>
                    </td>
                );
            }
        };
    }

    public renderModalDelete() {
        const curEditData = _.get(this.state, 'editData', {});
        const listName = Formatters.getStr(curEditData, 'name');
        const listId = Formatters.getStr(curEditData, '_id');

        return (
            <Modal enforceFocus={false} show={this.state.showModalDelete} onHide={() => this.handleCloseModalDelete()}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>Delete Regional Component List</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div>
                        <p>Are you sure you want to delete the regional component list:</p>
                        <p><strong>{listName}</strong> (ID: {listId})</p>
                        <p>This action cannot be undone.</p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="danger" onClick={(e) => this.handleConfirmDelete(e)}>Delete</Button>
                    <Button bsStyle="default" onClick={() => this.handleCloseModalDelete()}>Cancel</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public render() {
        const { regionalComponentLists, lookups, ...rest } = this.props;

        const lookupData = Array.isArray(regionalComponentLists)
            ? regionalComponentLists
            : (_.get(regionalComponentLists, 'results', []) || []);
        let lookupDataTransformed = this.extendGridData(lookupData);

        let gridProps: any = {};
        let gridCols = _.stubArray();

        // Define grid columns: Region, List Type, Description, Edit/Duplicate/Delete
        gridCols.push(this.getGridCol('regionName', 'Region', { width: '200px', editable: false, sortable: true }));
        gridCols.push(this.getGridCol('listType', 'List Type', { editable: false, sortable: true }));
        gridCols.push(this.getGridCol('descriptionPreview', 'Description', { width: '320px', editable: false, sortable: true, cell: this.renderDescriptionPreviewCell() }));

        const editGridCommandCell = this.renderGridCommandCell();
        gridCols.push(this.getGridCol('', '', { width: '275px', cell: editGridCommandCell }));

        gridProps.sortable = true;
        const defaultGridSort = [{ field: 'regionName', dir: 'asc' }, { field: 'listType', dir: 'asc' }];
        const curStateGridSortRaw = _.get(this.state, 'gridSort', defaultGridSort);
        const curStateGridSort = (_.isEmpty(curStateGridSortRaw)) ? defaultGridSort : curStateGridSortRaw;
        gridProps.sort = (_.size(curStateGridSort) > 0) ? curStateGridSort : defaultGridSort;
        gridProps.sortChange = (e: any) => {
            const eventGridSortRaw = _.get(e, 'sort', defaultGridSort);
            const eventGridSortNext = (_.isEmpty(eventGridSortRaw)) ? defaultGridSort : eventGridSortRaw;
            this.setState({ 'gridSort': eventGridSortNext });
        };

        lookupDataTransformed = orderBy(lookupDataTransformed, curStateGridSort);

        const gridDataSource = process(lookupDataTransformed, {});

        const grid = (
            <Grid
                data={gridDataSource}
                resizable={true}
                editField={this.GridEditField}
                {...gridProps}
            >
                {_.map(gridCols, (c, i) => this.renderGridCol(c, i))}
            </Grid>
        );

        const pageTitle = 'Regional Component Lists';
        const page = (
            <div>
                <Row>
                    <Col xs={12} xl={9}>
                        <div>
                            {this.renderHeaderButtonToolbar()}
                            {this.renderModalDelete()}
                        </div>
                    </Col>
                </Row>
                <WhiteBoxPageSection title={pageTitle}>
                    <Row>
                        <Col xs={12}>
                            {grid}
                        </Col>
                    </Row>
                </WhiteBoxPageSection>
            </div>
        );

        return (
            <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={pageTitle} {...rest}>
                {page}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getApiUrlLookups = () => ApiEndpoints.getLookupsByKeyUrl([
    LK.LookupKeys.Regions,
]);

const getApiUrl = () => (ApiEndpoints as any).getRegionalComponentListsUrl();

const mapPropsToConfigs = (props) => {
    return [
        ReduxQueries.Queries.getLookupsByKeyRequest(
            getApiUrlLookups(),
            ReduxQueries.PropsAccessToken(props)
        ),
        (ReduxQueries.Queries as any).getRegionalComponentListsRequest(
            getApiUrl(),
            ReduxQueries.PropsAccessToken(props)
        ),
    ];
};

const mapStateToProps = (state, props) => {
    return {
        ...ReduxQueries.MapQueryStateToPropsByUrl(getApiUrlLookups(), state, props, {
            lookups: state.entities.lookupsByKey,
        }),
        ...ReduxQueries.MapQueryStateToPropsByUrl(getApiUrl(), state, props, {
            regionalComponentLists: state.entities.regionalComponentListsResults,
        }),
    };
};

const mapDispatchToProps = {
    deleteRegionalComponentListObj: (MutateActions as any).deleteRegionalComponentListMutateAsync,
    duplicateRegionalComponentListObj: (MutateActions as any).duplicateRegionalComponentListMutateAsync,
    newRegionalComponentList: (MutateActions as any).newRegionalComponentListMutateAsync,
    navigateTo: (path) => push(path),
};

const RegionalComponentListManagerContainer = compose<any>(
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest(mapPropsToConfigs)
)(RegionalComponentListManager);

export default withRouter(RegionalComponentListManagerContainer);



// WEBPACK FOOTER //
// ./src/regional-component-lists/index.tsx